import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useLogout } from "react-admin";
import { IconButton, ButtonBase, useMediaQuery, Tooltip } from "@material-ui/core";
import { Box, Typography, Stack } from "@mui/material";
import BookIcon from "@material-ui/icons/Book";
import LinkIcon from "@material-ui/icons/Link";
import AssignmentIcon from "@material-ui/icons/Assignment";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import PermMediaIcon from "@material-ui/icons/PermMedia";
import httpClient from "../../../lib/httpClient";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { withStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";

import { getLatestUpdateDaily, getLatestUpdateAgro, formatDateValue } from "../../util/tableUtils";
import { userHasSomePermissions } from "../../util/permissionsUtils";
import { saveUserMonitorData } from "../../util/userUtils";
import { useClientConfig } from "../../../context/clientConfigContext";
import { PermissionIdEnum } from "../../util/types/permissions/permissionIdEnum";

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: "#777777",
    color: "#ffffff",
    boxShadow: theme.shadows[1],
    fontSize: 18,
  },
}))(Tooltip);

const textGreyColor = "#e7d3f5";

export default function MissionControlOverlay() {
  const { t } = useTranslation();
  const [connectionStatus, setConnectionStatus] = useState("");
  const [lastUpdateDatePoliciesMsg, setLastUpdateDatePoliciesMsg] = useState("");
  const [lastUpdateDateAgricultureMsg, setLastUpdateDateAgricultureMsg] = useState("");
  const [buildingPoliciesActive, setBuildingPolicesActive] = useState("...");
  const [agroPolicesActive, setAgroPolicesActive] = useState("...");
  const [currentYear, setCurrentYear] = useState("...");

  const logout = useLogout();
  const max600 = useMediaQuery("(max-width:600px)");
  const config = useClientConfig();

  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    if (user && user.accessToken) {
      const user = JSON.parse(localStorage.getItem("user"));
      setConnectionStatus(t("mission.hello", { name: user?.name, clientDomainName: config.domainName }));

      Promise.all([
        getLatestUpdateDaily("/serviceLogs/buildings?service=db_buildings_unique_multi"),
        getLatestUpdateAgro("/serviceLogs/agriculture?service=agricultureDb_contratti"),
        httpClient().get("/dashboard"),
      ]).then(([lastUpdateMsgPolicies, lastUpdateMsgAgriculture, dashboardData]) => {
        setLastUpdateDatePoliciesMsg(lastUpdateMsgPolicies);
        setLastUpdateDateAgricultureMsg(lastUpdateMsgAgriculture);

        setBuildingPolicesActive(dashboardData.data.buildingPoliciesActive);
        setAgroPolicesActive(dashboardData.data.agroPoliciesActive);
        setCurrentYear(dashboardData.data.currentYear);
      });

      saveUserMonitorData("missionControl", "");
    }
  }, []);

  const iconB = (
    <LightTooltip title={t("mission.buildingsMsg")}>
      <IconButton>
        <HelpOutlineIcon style={{ color: textGreyColor }} />
      </IconButton>
    </LightTooltip>
  );

  const iconA = (currentYear) => (
    <LightTooltip title={t("mission.agricultureMsg", { year: currentYear })}>
      <IconButton>
        <HelpOutlineIcon style={{ color: textGreyColor }} />
      </IconButton>
    </LightTooltip>
  );

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          zIndex: 1000,
          top: 0,
          padding: "50px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "space-between",
          height: "100vh",
          boxSizing: "border-box",
          width: max600 ? "100%" : "auto",
          backdropFilter: "blur(8px)",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ margin: 0, color: textGreyColor, fontWeight: 600 }}>
            {connectionStatus}
          </Typography>

          {/* Buildings Policies */}
          {userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.MenuPoliciesBuildings]) && (
            <>
              <Typography variant="h6" sx={{ margin: 0, color: textGreyColor, fontWeight: 600 }}>
                {t("policy.title")}: {buildingPoliciesActive} {t("mission.active")} {iconB}
              </Typography>
              <Typography variant="body2" sx={{ margin: 0, color: textGreyColor, fontWeight: 600 }}>
                {lastUpdateDatePoliciesMsg}
              </Typography>
            </>
          )}

          {/* Agriculture Policies */}
          {userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.MenuPoliciesAgriculture]) && (
            <>
              <Typography variant="h6" sx={{ margin: 0, color: textGreyColor, fontWeight: 600 }}>
                {t("policy.agricultureMenu")}: {agroPolicesActive} {iconA(currentYear)}
              </Typography>
              <Typography variant="body2" sx={{ margin: 0, color: textGreyColor, fontWeight: 600 }}>
                {lastUpdateDateAgricultureMsg}
              </Typography>
            </>
          )}
        </Box>

        <Stack spacing={2}>
          {userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.MenuPoliciesBuildings]) && (
            <LinkButton
              label={t("policy.title")}
              icon={<AssignmentIcon style={{ fontSize: 20 }} />}
              backgroundColor="#f49342"
              to="/newpolicies"
            />
          )}

          {userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.MenuClaimsBuildings]) && (
            <LinkButton
              label={t("claims.title")}
              icon={<AssignmentIcon style={{ fontSize: 20 }} />}
              backgroundColor="#f49342"
              to="/newclaims"
            />
          )}

          {userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.MenuPoliciesAgriculture]) && (
            <LinkButton
              label={t("policy.agricultureMenu")}
              icon={<PermMediaIcon style={{ fontSize: 20 }} />}
              backgroundColor="#47c0bf"
              to="/policiesagriculture"
            />
          )}

          {userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.MenuClaimsAgriculture]) && (
            <LinkButton
              label={t("claims.agricultureMenu")}
              icon={<PermMediaIcon style={{ fontSize: 20 }} />}
              backgroundColor="#47c0bf"
              to="/claimsagriculture"
            />
          )}
        </Stack>
      </Box>

      <Tooltip title="Logout" aria-label="logout">
        <IconButton
          style={{ position: "absolute", zIndex: 1000, top: 20, right: 20 }}
          onClick={() => logout()}
        >
          <ExitToAppIcon style={{ color: textGreyColor }} />
        </IconButton>
      </Tooltip>

      {config.showHelpCenter && (
        <Tooltip title="Help Center" aria-label="logout">
          <IconButton
            style={{ position: "absolute", zIndex: 1000, top: 20, right: 80 }}
            component="a"
            href="http://help.realerisk.s3-website-eu-west-1.amazonaws.com/"
            target="_blank"
          >
            <BookIcon style={{ color: textGreyColor }} />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
}

const LinkButton = ({ label, icon, backgroundColor, to, href }) => {
  const max600 = useMediaQuery("(max-width:600px)");
  const history = useHistory();

  return (
    <ButtonBase
      style={{
        width: max600 ? "100%" : 250,
        backgroundColor,
        padding: 10,
        borderRadius: 10,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        opacity: 0.9,
      }}
      onClick={() => (href ? window.open(href, "_blank", "noopener,noreferrer") : history.push(to))}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            height: 40,
            width: 40,
            borderRadius: 20,
            backgroundColor: "rgba(256,256,256,.25)",
            color: "#FFF",

            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {icon}
        </div>

        <h3 style={{ textAlign: "left", color: "#FFF" }}>{label}</h3>
      </div>

      <LinkIcon style={{ transform: "rotate(-45deg)", color: "#FFF" }} />
    </ButtonBase>
  );
};
